// src/lib/dataExport.ts
import { turso } from '@/database/client';

export interface UserDataExport {
  exportDate: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  calendarEvents: any[];
  preferences: any;
  metadata: {
    version: string;
    itemCount: number;
  };
}

/**
 * Export all user data as JSON
 */
export async function exportUserData(userId: string): Promise<UserDataExport> {
  try {
    // Get user info
    const userResult = await turso.execute({
      sql: 'SELECT id, name, email, created_at FROM users WHERE id = ?',
      args: [userId]
    });

    if (!userResult.rows || userResult.rows.length === 0) {
      throw new Error('User not found');
    }

    const userRow = userResult.rows[0];

    // Get calendar events
    const calendarResult = await turso.execute({
      sql: 'SELECT * FROM calendar_events WHERE user_id = ? ORDER BY due_date ASC',
      args: [userId]
    });

    const calendarEvents = calendarResult.rows.map(row => ({
      id: row.id,
      title: row.title,
      description: row.description,
      due_date: row.due_date,
      course: row.course,
      status: row.status,
      category: row.category,
      notes: row.notes,
      created_at: row.created_at,
      updated_at: row.updated_at
    }));

    // Get user preferences
    const preferencesResult = await turso.execute({
      sql: "SELECT data_key, data_value FROM user_data WHERE user_id = ? AND data_type = 'preferences'",
      args: [userId]
    });

    const preferences: any = {};
    preferencesResult.rows.forEach(row => {
      try {
        preferences[row.data_key as string] = JSON.parse(row.data_value as string);
      } catch {
        preferences[row.data_key as string] = row.data_value;
      }
    });

    // Build export object
    const exportData: UserDataExport = {
      exportDate: new Date().toISOString(),
      user: {
        id: userRow.id as string,
        name: userRow.name as string,
        email: userRow.email as string
      },
      calendarEvents,
      preferences,
      metadata: {
        version: '1.0.0',
        itemCount: calendarEvents.length
      }
    };

    return exportData;
  } catch (error) {
    console.error('[Export] Failed to export user data:', error);
    throw error;
  }
}

/**
 * Download user data as JSON file
 */
export async function downloadUserData(userId: string, fileName?: string): Promise<void> {
  try {
    const data = await exportUserData(userId);
    
    // Create blob
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || `whipt-data-export-${new Date().toISOString().split('T')[0]}.json`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    URL.revokeObjectURL(url);
    
    console.log('[Export] Data exported successfully');
  } catch (error) {
    console.error('[Export] Failed to download user data:', error);
    throw error;
  }
}

/**
 * Import user data from JSON
 * WARNING: This will overwrite existing data!
 */
export async function importUserData(userId: string, jsonData: string): Promise<{ success: boolean; imported: number; errors: number }> {
  try {
    const data: UserDataExport = JSON.parse(jsonData);
    
    let imported = 0;
    let errors = 0;

    // Import calendar events
    for (const event of data.calendarEvents) {
      try {
        // Delete existing event if it exists
        await turso.execute({
          sql: 'DELETE FROM calendar_events WHERE id = ?',
          args: [event.id]
        });

        // Insert event
        await turso.execute({
          sql: `INSERT INTO calendar_events (id, user_id, title, description, due_date, course, status, category, notes, created_at, updated_at)
                VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          args: [
            event.id,
            userId, // Use current user's ID
            event.title,
            event.description || null,
            event.due_date,
            event.course || null,
            event.status || 'pending',
            event.category || null,
            event.notes || null,
            event.created_at || new Date().toISOString(),
            new Date().toISOString() // Update timestamp
          ]
        });

        imported++;
      } catch (err) {
        console.error('[Import] Failed to import event:', event.id, err);
        errors++;
      }
    }

    // Import preferences
    for (const [key, value] of Object.entries(data.preferences)) {
      try {
        const dataId = `userdata_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Delete existing preference
        await turso.execute({
          sql: "DELETE FROM user_data WHERE user_id = ? AND data_type = 'preferences' AND data_key = ?",
          args: [userId, key]
        });

        // Insert preference
        await turso.execute({
          sql: `INSERT INTO user_data (id, user_id, data_type, data_key, data_value, created_at, updated_at)
                VALUES (?, ?, 'preferences', ?, ?, datetime('now'), datetime('now'))`,
          args: [dataId, userId, key, JSON.stringify(value)]
        });

        imported++;
      } catch (err) {
        console.error('[Import] Failed to import preference:', key, err);
        errors++;
      }
    }

    console.log(`[Import] Import complete: ${imported} imported, ${errors} errors`);
    
    return {
      success: errors === 0,
      imported,
      errors
    };
  } catch (error) {
    console.error('[Import] Failed to import user data:', error);
    throw error;
  }
}

/**
 * Create a backup of user data
 * Saves to localStorage as emergency backup
 */
export function createLocalBackup(data: UserDataExport): void {
  try {
    const backupKey = `whipt_backup_${data.user.id}_${Date.now()}`;
    localStorage.setItem(backupKey, JSON.stringify(data));
    
    // Keep only last 3 backups
    const allBackups = Object.keys(localStorage)
      .filter(key => key.startsWith(`whipt_backup_${data.user.id}_`))
      .sort()
      .reverse();
    
    // Remove old backups
    allBackups.slice(3).forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log('[Backup] Created local backup:', backupKey);
  } catch (error) {
    console.error('[Backup] Failed to create local backup:', error);
  }
}
